import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../utils/axiosConfig";
import { API_URL } from "../../constant";
import { useAuth } from "../../context/auth-context";
import { TailSpin } from "react-loader-spinner";

type LevelLimits = { tradingLimit: number; withdrawalLimit: number };
type LevelLimitsDisplay = {
  tradingLimit: number | "";
  withdrawalLimit: number | "";
};
type LimitsData = {
  level1: LevelLimits;
  level2: LevelLimits;
  level3: LevelLimits;
};
type LimitsDisplay = {
  level1: LevelLimitsDisplay;
  level2: LevelLimitsDisplay;
  level3: LevelLimitsDisplay;
};

const defaultLimits: LimitsData = {
  level1: { tradingLimit: 0, withdrawalLimit: 0 },
  level2: { tradingLimit: 0, withdrawalLimit: 0 },
  level3: { tradingLimit: 0, withdrawalLimit: 0 },
};

const toDisplay = (d: LimitsData): LimitsDisplay => ({
  level1: {
    tradingLimit: d.level1.tradingLimit,
    withdrawalLimit: d.level1.withdrawalLimit,
  },
  level2: {
    tradingLimit: d.level2.tradingLimit,
    withdrawalLimit: d.level2.withdrawalLimit,
  },
  level3: {
    tradingLimit: d.level3.tradingLimit,
    withdrawalLimit: d.level3.withdrawalLimit,
  },
});

const KycLimits = () => {
  const { isAuthenticated } = useAuth();
  const [limits, setLimits] = useState<LimitsDisplay>(toDisplay(defaultLimits));
  const [lastFetched, setLastFetched] = useState<LimitsData>(defaultLimits);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");

  const fetchLimits = useCallback(() => {
    if (!isAuthenticated) return;
    setLoading(true);
    setError("");
    axiosInstance
      .get(`${API_URL}api/settings/limits`)
      .then((r: any) => {
        const data = r.data?.data ?? defaultLimits;
        const normalized: LimitsData = {
          level1: {
            tradingLimit: Number(data.level1?.tradingLimit) || 0,
            withdrawalLimit: Number(data.level1?.withdrawalLimit) || 0,
          },
          level2: {
            tradingLimit: Number(data.level2?.tradingLimit) || 0,
            withdrawalLimit: Number(data.level2?.withdrawalLimit) || 0,
          },
          level3: {
            tradingLimit: Number(data.level3?.tradingLimit) || 0,
            withdrawalLimit: Number(data.level3?.withdrawalLimit) || 0,
          },
        };
        setLimits(toDisplay(normalized));
        setLastFetched(normalized);
      })
      .catch((e: any) => {
        setError(e.response?.data?.message ?? "Failed to load limits");
      })
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  useEffect(() => {
    fetchLimits();
  }, [fetchLimits]);

  const updateLevel = (
    level: keyof LimitsData,
    field: keyof LevelLimits,
    value: number | ""
  ) => {
    setLimits((prev) => ({
      ...prev,
      [level]: { ...prev[level], [field]: value },
    }));
  };

  const numOrFetched = (val: number | "", fetched: number): number =>
    val === "" || (typeof val === "number" && Number.isNaN(val))
      ? fetched
      : Number(val);

  const handleSave = () => {
    if (!isAuthenticated || saving) return;
    const body: LimitsData = {
      level1: {
        tradingLimit: numOrFetched(
          limits.level1.tradingLimit,
          lastFetched.level1.tradingLimit
        ),
        withdrawalLimit: numOrFetched(
          limits.level1.withdrawalLimit,
          lastFetched.level1.withdrawalLimit
        ),
      },
      level2: {
        tradingLimit: numOrFetched(
          limits.level2.tradingLimit,
          lastFetched.level2.tradingLimit
        ),
        withdrawalLimit: numOrFetched(
          limits.level2.withdrawalLimit,
          lastFetched.level2.withdrawalLimit
        ),
      },
      level3: {
        tradingLimit: numOrFetched(
          limits.level3.tradingLimit,
          lastFetched.level3.tradingLimit
        ),
        withdrawalLimit: numOrFetched(
          limits.level3.withdrawalLimit,
          lastFetched.level3.withdrawalLimit
        ),
      },
    };
    setSaving(true);
    setError("");
    setSuccessMessage("");
    axiosInstance
      .patch(`${API_URL}api/settings/limits`, body)
      .then(() => {
        setSuccessMessage("Limits updated successfully");
        fetchLimits();
        setTimeout(() => setSuccessMessage(""), 3000);
      })
      .catch((e: any) => {
        setError(e.response?.data?.message ?? "Failed to update limits");
      })
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <div className="px-8 mds:px-12 lgss:px-20 pb-16 pt-16 flex justify-center items-center min-h-[200px]">
        <TailSpin color="#0C84DA" height={40} width={40} />
      </div>
    );
  }

  return (
    <div className="px-8 mds:px-12 lgss:px-20 pb-16 pt-16">
      <h4 className="text-[18px] font-semibold text-white mb-6">KYC limits</h4>
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-500/10 text-red-400 text-[14px]">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="mb-4 p-3 rounded-lg bg-[#0E2B1F] text-[#03C668] text-[14px]">
          {successMessage}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {(["level1", "level2", "level3"] as const).map((levelKey) => (
          <div
            key={levelKey}
            className="rounded-xl border border-[#1C2227] bg-[#141414] p-5"
          >
            <h4 className="text-white font-semibold text-[16px] mb-4 capitalize">
              {levelKey.replace("level", "Level ")}
            </h4>
            <div className="space-y-4">
              <div>
                <label className="text-[#869AA9] text-[12px] font-medium block mb-1">
                  Trading limit
                </label>
                <input
                  type="number"
                  min={0}
                  value={limits[levelKey].tradingLimit}
                  onChange={(e) => {
                    const v = e.target.value;
                    updateLevel(
                      levelKey,
                      "tradingLimit",
                      v === "" ? "" : Number(v) || 0
                    );
                  }}
                  className="w-full h-[44px] px-3 rounded-lg bg-[#0D0D0D] border border-[#1C2227] text-white text-[14px] outline-none focus:border-[#0C84DA]"
                />
              </div>
              <div>
                <label className="text-[#869AA9] text-[12px] font-medium block mb-1">
                  Withdrawal limit
                </label>
                <input
                  type="number"
                  min={0}
                  value={limits[levelKey].withdrawalLimit}
                  onChange={(e) => {
                    const v = e.target.value;
                    updateLevel(
                      levelKey,
                      "withdrawalLimit",
                      v === "" ? "" : Number(v) || 0
                    );
                  }}
                  className="w-full h-[44px] px-3 rounded-lg bg-[#0D0D0D] border border-[#1C2227] text-white text-[14px] outline-none focus:border-[#0C84DA]"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-8">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-3 rounded-xl bg-secondary_button text-white font-medium text-[14px] disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? <TailSpin color="#FFFFFF" height={20} width={20} /> : null}
          Update limits
        </button>
      </div>
    </div>
  );
};

export default KycLimits;
